<template>
  <div>
    <div class="row q-gutter-md q-mb-sm">
      <model-form-input
        field="config.voucherWidgetTitle"
        value-type="String"
        class="col"
        label="Title"
        :disable="!section.config.showVoucherWidgetTitle"
        :rules="[val => val !== null || 'Field is required']"
      />
      <model-form-toggle
        field="config.showVoucherWidgetTitle"
        value-type="Boolean"
        class="col-1 q-my-auto q-mr-md"
        label="Title"
        size="lg"
      />
      <model-form-toggle
        field="config.showVoucherWidgetCart"
        value-type="Boolean"
        class="col-1 q-my-auto q-mr-md"
        label="Cart"
        size="lg"
      />
    </div>
    <div class="row q-col-gutter-md q-mb-md items-start">
      <color-selection
        class="col"
        :color-name="section.config.voucherWidgetBackgroundColor"
        :custom-color-hex="section.config.voucherWidgetBackgroundColorHex"
        label="Vouchers Background Color"
        :disable="section.isTranslation"
        @update:model-value="setBackgroundColorInput"
      />
      <color-selection
        class="col"
        :color-name="section.config.voucherWidgetTextColor"
        :custom-color-hex="section.config.voucherWidgetTextColorHex"
        label="Vouchers Text Color"
        :disable="section.isTranslation"
        @update:model-value="setTextColorInput"
      />
    </div>
    <div class="row q-gutter-md q-mb-xl">
      <q-table
        v-model:pagination="pagination"
        class="col"
        :columns="voucherSalesColumns"
        :rows="section.config.voucherSales"
        :rows-per-page-options="[0]"
        hide-pagination
        bordered
        flat
        wrap-cells
        row-key="key"
      >
        <template #top>
          <div class="q-table__title">
            Voucher Sales
          </div>

          <q-space />

          <q-btn
            class="q-mx-sm"
            label="Set Order"
            @click="$refs.orderDialog.show()"
          />
          <q-btn
            label="Add Voucher Sale"
            outline
            @click="editVoucherSale(null)"
          />
        </template>

        <template
          #body-cell-actions="props"
        >
          <q-td
            :props="props"
          >
            <slot
              name="row-actions"
              v-bind="props"
            >
              <div class="row q-gutter-sm justify-end no-wrap">
                <slot
                  name="prepend-row-actions"
                  v-bind="props"
                />
                <q-btn
                  :hover="false"
                  label="Edit"
                  @click="editVoucherSale(props.row)"
                />
                <q-btn
                  :hover="false"
                  icon="more_vert"
                  round
                  @click.stop
                >
                  <q-menu>
                    <q-list style="min-width: 180px">
                      <q-item
                        v-close-popup
                        clickable
                        @click="editVoucherSale(props.row)"
                      >
                        <q-item-section avatar>
                          <q-icon name="edit" />
                        </q-item-section>
                        <q-item-section>
                          Edit
                        </q-item-section>
                      </q-item>
                      <q-separator />
                      <q-item
                        v-close-popup
                        clickable
                        @click="deleteVoucherSale(props.row)"
                      >
                        <q-item-section avatar>
                          <q-icon name="delete" />
                        </q-item-section>
                        <q-item-section>
                          {{ $t('actions.remove') }}
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </q-menu>
                </q-btn>
              </div>
            </slot>
          </q-td>
        </template>
      </q-table>
    </div>
    <preview
      :section="section"
      class="q-mb-xl"
    />

    <set-display-order-dialog
      ref="orderDialog"
      :list="section.config.voucherSales"
      @reorder="handleReorder"
    />
    <voucher-sale-form
      ref="voucherSaleForm"
      :section="section"
      @save="voucherSale => saveVoucherSale(voucherSale)"
    />
  </div>
</template>

<script>
import { LocalizedJsonObject } from 'src/models/model'
import CheckoutData from 'src/models/order/CheckoutData'
import Order from 'src/models/order/Order'
import Section from 'src/models/page/Section'
import ColorSelection from 'src/modules/admin/components/ColorSelection'
import SetDisplayOrderDialog from 'src/modules/admin/components/SetDisplayOrderDialog'
import VoucherSaleForm from 'src/modules/admin/section/VoucherSaleForm.vue'
import Preview from 'src/modules/consumer/components/Preview'

export default {
  name: 'VouchersWidgetEditor',
  components: {
    Preview,
    VoucherSaleForm,
    SetDisplayOrderDialog,
    ColorSelection
  },
  props: {
    section: {
      type: Section,
      required: true
    }
  },
  data () {
    return {
      checkoutData: new CheckoutData(),
      order: new Order({}, {
        orderItems: {
          voucherPurchase: {
            voucherType: true,
            card: 'default'
          }
        }
      }),
      voucherSalesColumns: [
        { name: 'name', align: 'left', label: 'Name', field: (voucherSale) => voucherSale[this.localizeKey('name')] || voucherSale.name, sortable: true },
        { name: 'voucherTypeName', align: 'left', label: 'Voucher Type', field: 'voucherTypeName', sortable: true },
        { name: 'minimumAmount', align: 'left', label: 'Minimum Amount', field: 'minimumAmount' },
        { name: 'actions', label: 'Actions' }
      ],
      pagination: {
        page: 1,
        rowsPerPage: 0
      }
    }
  },
  unmounted () {
    this.order?.destroy()
  },
  methods: {
    saveVoucherSale (voucherSale) {
      const index = _.findIndex(this.section.config.voucherSales, { key: voucherSale.key })
      if (index === -1) {
        this.section.config.voucherSales.push(voucherSale)
      } else {
        this.section.config.voucherSales[index] = voucherSale
      }
    },
    editVoucherSale (voucherSale = null) {
      const index = voucherSale ? _.findIndex(this.section.config.voucherSales, { key: voucherSale.key }) : null
      this.$refs.voucherSaleForm.show(index)
    },
    deleteVoucherSale (voucherSale) {
      const index = _.findIndex(this.section.config.voucherSales, { key: voucherSale.key })
      this.section.config.voucherSales.splice(index, 1)
    },
    handleReorder ({ reorderedList }) {
      this.section.config.voucherSales = reorderedList
    },
    localizeKey (key) {
      const localizedKey = LocalizedJsonObject.localizeKey(key, this.section.language)[0]
      return localizedKey
    },
    setBackgroundColorInput (colorName, customColorHex) {
      this.section.config.voucherWidgetBackgroundColor = colorName
      this.section.config.voucherWidgetBackgroundColorHex = customColorHex
    },
    setTextColorInput (colorName, customColorHex) {
      this.section.config.voucherWidgetTextColor = colorName
      this.section.config.voucherWidgetTextColorHex = customColorHex
    }
  }
}

</script>
